import { Component } from 'react'
import PropTypes from 'prop-types'
import { DeseretBook } from '../../dbcom/api'
import { Logger, formatErrorMessage } from '../../dbcom'
import { isPlatinum } from '../isPlatinum'
import { FullError } from '../../errors'

const logger = new Logger({ context: 'SubscriptionFormManager' })

/**
 * Manages all the data and stuff needed for a subscription form.
 */
class SubscriptionFormManager extends Component {
  /**
   * Component constructor
   */
  constructor() {
    super()

    this.state = {
      serviceIsAvailable: true,
      requiresPhysicalAddress: false,
      membershipDiscountPlans: [],
      userCanCreate: true,
      plans: [],
      estimates: [],
      addresses: [],
      paymentSources: [],
      form: {},
      giftCardForm: {
        number: '',
      },
      giftCard: null,
      isInitialized: false,
      isFetchingSubscriptionPlans: true,
      isFetchingAddresses: true,
      isFetchingPaymentSources: true,
      isFetchingHealth: true,
      isFetchingEstimates: true,
      isFetchingGiftCard: false,
      isCreating: false,
    }

    this.onGiftCardFormChange = this.onGiftCardFormChange.bind(this)
    this.onFormChange = this.onFormChange.bind(this)
    this.initialize = this.initialize.bind(this)
    this.refreshAddresses = this.refreshAddresses.bind(this)
    this.refreshPaymentSources = this.refreshPaymentSources.bind(this)
    this.refreshEstimates = this.refreshEstimates.bind(this)
    this.applyGiftCard = this.applyGiftCard.bind(this)
    this.removeGiftCard = this.removeGiftCard.bind(this)
    this.createSubscription = this.createSubscription.bind(this)
  }

  /**
   * Gets data and initializes everything once the component is mounted.
   */
  componentDidMount() {
    this.initialize()
  }

  /**
   * Run every time the component updates. Determines if the price estimate needs to be updated
   * or not.
   *
   * @param {object} prevProps - Previous component props
   * @param {object} prevState - Previous component state
   */
  componentDidUpdate(prevProps, { form, isInitialized }) {
    const paymentChanged = form.walletSourceId !== this.state.form.walletSourceId
    const deliveryChanged = form.shippingAddressId !== this.state.form.shippingAddressId
    const addonsChanged = form.addonSubscriptions !== this.state.form.addonSubscriptions

    if (isInitialized && (paymentChanged || deliveryChanged || addonsChanged)) {
      this.refreshEstimates()
    }
  }

  /**
   * Handles updates to gift card form fields.
   *
   * @param {event} event - input change event
   * @param {HTMLElement} event.target - Event target
   */
  onGiftCardFormChange({ target }) {
    this.setState(({ giftCardForm }) => ({
      giftCardForm: {
        ...giftCardForm,
        [target.name]: target.value,
      },
    }))
  }

  /**
   * Handles updates to subscription form fields.
   * @param {event} event - input change event
   * @param {HTMLElement} event.target - Event target
   */
  onFormChange({ target }) {
    this.setState(
      ({ form, paymentSources }) => {
        // Some changes to fields require changes to other stuff, so we'll figure all that out
        // and put those changes in here and apply them all at the end.
        const formChanges = {}

        // If the walletSourceId is changing then we need to update billing address as well.
        if (target.name === 'walletSourceId') {
          const source = paymentSources.find(({ id }) => id === +target.value) || {}

          formChanges.creditCardId = source && source.paymentSource && source.paymentSource.id
        }

        // We have to store the extra subscriptions somewhere, so we'll add them via a checkbox
        // but really we'll store all the checked values in an array.
        if (target.name === 'subscriptionAddon') {
          const btPlanId = target.value
          const updatedAddons = form.addonSubscriptions.filter((planId) => planId !== btPlanId)

          // Add the checked subscription to the list.
          if (target.checked) {
            updatedAddons.push(target.value)
          }

          formChanges.addonSubscriptions = updatedAddons
        }

        // Add the actual form value unless it's one we really don't want.
        if (target.name !== 'subscriptionAddon') {
          formChanges[target.name] = target.value
        }

        return {
          form: {
            ...form,
            ...formChanges,
          },
        }
      },
      () => {
        if (typeof this.props.onFormChange === 'function') {
          this.props.onFormChange(target)
        }
      },
    )
  }

  /**
   * Fetches initially critical data. This fetches and sets up everything that is needed for
   * initial rendering. Less critical requests are made with the `getSecondaryData` method.
   */
  async initialize() {
    const { subscriptionType, user } = this.props

    // Subscriptions require a logged in user, so if we don't have that then we can't really do
    // anything so we just stop right here.
    if (!user || !user.id) {
      this.setState({
        userCanCreate: false,
        isInitialized: true,
        isFetchingSubscriptionPlans: false,
        isFetchingAddresses: false,
        isFetchingPaymentSources: false,
        isFetchingHealth: false,
        isFetchingEstimates: false,
      })
    }

    // Since there's lots of stuff to figure out, we're going to go one-by-one and get all the data
    // we need and update at the very end.
    const state = {}

    // We need info about each subscription plan of the specified type, as well as Platinum
    // subscriptions because we might need it to up-sell Platinum subscriptions as well.
    const subscriptionPlansRequest = DeseretBook.subscriptionPlan()
      .all({
        q: {
          availableToUsersEq: true,
          billableEq: true,
          sorts: 'price asc',
        },
      })
      // Return an empty object if this fails.
      .catch(() => ({}))

    // We need the user's credit cards because subscriptions cost money.
    const userWalletRequest = DeseretBook.user(user)
      .walletPaymentSource()
      .all({ perPage: 1000, page: 1 })

    // We need to do a health check on our services so we know if we're even able to create
    // subscriptions right now.
    const healthCheckRequest = DeseretBook.health()

    try {
      // Fetch all the API data we need.
      const [
        { subscriptionPlans = [] },
        { walletPaymentSources = [] },
        { braintree = false },
      ] = await Promise.all(
        [subscriptionPlansRequest, userWalletRequest, healthCheckRequest].map((promise) =>
          promise.catch(() => ({})),
        ),
      )

      // Now that we have all the API data we need, we can extract some useful information.

      // Parse out the different subscription plans we have now.
      state.plans = subscriptionPlans.filter(({ type }) => type === subscriptionType)

      state.platinumMembershipPlan = subscriptionPlans.find(
        ({ type }) => type === 'SubscriptionPlan::Platinum',
      )

      // Group together subscription plans that provide a membership discount.
      state.membershipDiscountPlans = (
        (state.plans[0] || {}).availableMembershipDiscounts || []
      ).reduce((discountPlans, { sourcePlanType }) => {
        if (!sourcePlanType) {
          return discountPlans
        }

        return {
          ...discountPlans,
          [sourcePlanType]: subscriptionPlans.filter(({ type }) => type === sourcePlanType),
        }
      }, {})

      // Store wallet cards so they can be used for the subscription
      state.paymentSources = walletPaymentSources

      // Braintree is the only service we care about for creating subscriptions
      state.serviceIsAvailable = !!braintree

      // Figure out if the requested subscription type also requires physical delivery.
      state.requiresPhysicalAddress = subscriptionPlans.some(
        ({ physicalDelivery, type }) => physicalDelivery && type === subscriptionType,
      )

      // If the requested subscription type requires a physical address so we know if we need
      // to go get the user's address book as well.
      if (state.requiresPhysicalAddress) {
        state.addresses = await DeseretBook.user(user)
          .addressBook()
          .get()
      }

      // Initialize the form with all the data we know about.
      const source = state.paymentSources.find((paymentSource) => paymentSource.default) || {}
      const defaultAddress = state.addresses && state.addresses.find((address) => address.default)

      state.form = {
        btPlanId: state.plans.length && state.plans[0].btPlanId,
        walletSourceId: source && source.id,
        creditCardId: source && source.paymentSource && source.paymentSource.id,
        // billingAddress: source && source.paymentSource && source.paymentSource.address,
        shippingAddressId: state.requiresPhysicalAddress && defaultAddress && defaultAddress.id,
        // shippingAddress: state.requiresPhysicalAddress && defaultAddress,
        addonSubscriptions: [],
      }

      // Now that we have a form built we can get the estimates.

      // Since we're done fetching initial data, we need to indicate that the different requests
      // we make aren't being being made anymore.
      state.isFetchingSubscriptionPlans = false
      state.isFetchingPaymentSources = false
      state.isFetchingAddresses = false
      state.isFetchingHealth = false
      state.isFetchingEstimates = false

      // Great, that should be it, so we can update our state now.
      this.setState(state, () => {
        // Once our state is setup we can go try to get estimates for our subscriptions.
        // Once the estimates are all fetched then we can finally say that our form is totally
        // initialized as we'll have all the data that we need.
        this.refreshEstimates(() => this.setState({ isInitialized: true }))
      })
    } catch ({ message, errors }) {
      logger.critical(
        `Unable to initialize subscription signup form: "${formatErrorMessage(message, errors)}"`,
      )
    }
  }

  /**
   * Pulls the most recent addresses for the user from the API. This should be called if you know
   * (or are pretty sure) that the user's addresses have changed.
   *
   * @param {function} [callback=noop] - Callback that is run once payment sources are updated
   *  successfully.
   */
  refreshAddresses(callback = () => {}) {
    const { user, onRefreshAddresses } = this.props

    this.setState({ isFetchingAddresses: true }, async () => {
      try {
        const addresses = await DeseretBook.user(user)
          .addressBook()
          .get()

        this.setState(
          {
            addresses,
            isFetchingAddresses: false,
          },
          () => {
            if (typeof callback === 'function') {
              callback(addresses)
            }

            if (typeof onRefreshAddresses === 'function') {
              onRefreshAddresses(addresses)
            }
          },
        )
      } catch ({ message, errors }) {
        this.setState({ isFetchingAddresses: false })
        logger.error(
          `Unable to fetch the user's address book: "${formatErrorMessage(message, errors)}"`,
          { errors, user },
        )
      }
    })
  }

  /**
   * Refreshes all the subscription price estimate information.
   *
   * @param {function} [callback=noop] - Callback that is run once the estimates are updated
   * successfully.
   */
  refreshEstimates(callback = () => {}) {
    const { user, promoCode } = this.props

    this.setState({ isFetchingEstimates: true }, async () => {
      const { plans, membershipDiscountPlans, form, estimates } = this.state

      const estimatesForm = {
        ...form,
        promoCode,
      }

      try {
        // Gather together all the potential plans so we can then get estimates for them all.
        const planEstimateRequests = plans.map(({ btPlanId }) => {
          const plan = estimates[btPlanId]
          const availableMembershipDiscounts = form.addonSubscriptions.reduce(
            (discounts, addonBtPlanId) => {
              const addonPlan = estimates[addonBtPlanId]

              if (plan && plan.subscriptionPlan && addonPlan && addonPlan.subscriptionPlan) {
                const applicableDiscount = (
                  plan.subscriptionPlan.availableMembershipDiscounts || []
                ).find(({ sourcePlanType }) => sourcePlanType === addonPlan.subscriptionPlan.type)

                return [...discounts, applicableDiscount.code]
              }

              return discounts
            },
            [],
          )

          return DeseretBook.user(user)
            .subscription()
            .new({ ...estimatesForm, availableMembershipDiscounts, btPlanId })
        })
        const addonEstimateRequests = Object.values(membershipDiscountPlans)
          .reduce((allPlans, currentPlans) => [...allPlans, ...currentPlans], [])
          .filter(
            ({ type, btPlanId }) =>
              !(type === 'SubscriptionPlan::Platinum' && user && isPlatinum(user)) ||
              !(
                user &&
                (user.subscriptions || []).find(
                  (sub) =>
                    !sub.awaitingCompletion &&
                    (sub.btPlanId || sub.subscriptionPlan.btPlanId) === btPlanId,
                )
              ),
          )
          .map(({ btPlanId }) =>
            DeseretBook.user(user)
              .subscription()
              .new({ ...estimatesForm, btPlanId }),
          )

        const estimateResponses = await Promise.all(
          [...planEstimateRequests, ...addonEstimateRequests].map((p) => p.catch(() => undefined)),
        )

        this.setState(
          {
            // We'll group our estimates into an object with the btPlanId as the key to make it a
            // little more convenient to find things.
            estimates: estimateResponses.filter(Boolean).reduce(
              (grouped, estimate) => ({
                ...grouped,
                [estimate.subscriptionPlan.btPlanId]: estimate,
              }),
              {},
            ),
            isFetchingEstimates: false,
          },
          callback,
        )
      } catch ({ message, errors }) {
        this.setState({ isFetchingEstimates: false })
        logger.error(
          `Unable to fetch subscription price estimates: "${formatErrorMessage(message, errors)}"`,
          { errors, user },
        )
      }
    })
  }

  /**
   * Checks to see if the current gift card form can be used as part of the current subscription(s)
   * If it can be then we'll save info about the gift card and apply it to the form.
   *
   * @param {function} [onSuccess=noop] - onSuccess callback
   * @param {function} [onError=noop] - onError callback
   */
  applyGiftCard(onSuccess = () => {}, onError = () => {}) {
    const { onAddGiftCard, onAddGiftCardFailure } = this.props
    const { giftCardForm } = this.state

    this.setState({ isFetchingGiftCard: true }, async () => {
      try {
        const giftCard = await DeseretBook.giftCard().get(giftCardForm)

        if (giftCard.error) {
          throw new Error('Gift card not recognized')
        }

        this.setState(
          {
            giftCard,
            giftCardForm: {
              number: '',
            },
            isFetchingGiftCard: false,
          },
          () => {
            if (typeof onSuccess === 'function') {
              onSuccess(giftCard)
            }

            if (typeof onAddGiftCard === 'function') {
              onAddGiftCard(giftCard)
            }
          },
        )
      } catch (error) {
        const { message, errors } = error

        logger.error(
          `Unable to apply a gift card to a new subscription: "${formatErrorMessage(
            message,
            errors,
          )}"`,
          { errors, form: giftCardForm },
        )

        this.setState({ isFetchingGiftCard: false }, () => {
          if (typeof onError === 'function') {
            onError(error)
          }

          if (typeof onAddGiftCardFailure === 'function') {
            onAddGiftCardFailure(error)
          }
        })
      }
    })
  }

  /**
   * Removes a gift card from being used on a subscription.
   */
  removeGiftCard() {
    const { onRemoveGiftCard } = this.props

    this.setState({ giftCard: null }, () => {
      if (typeof onRemoveGiftCard === 'function') {
        onRemoveGiftCard()
      }
    })
  }

  /**
   * Creates the current subscription. Stops if any data is missing.
   */
  createSubscription() {
    const { onCreateSuccessful, onCreateFailure, user, promoCode } = this.props

    this.setState({ isCreating: true }, async () => {
      const { form, giftCard } = this.state

      const createForm = {
        ...form,
        promoCode,
        giftCardNumber: giftCard && giftCard.number,
      }

      try {
        let createdSubscriptions = []

        if (form.addonSubscriptions && form.addonSubscriptions.length) {
          const addonSubscriptionRequests = form.addonSubscriptions.map((btPlanId) =>
            DeseretBook.user(user)
              .subscription()
              .create({ ...createForm, btPlanId }),
          )

          const addons = await Promise.all(addonSubscriptionRequests)

          createdSubscriptions = addons
        }

        const createdSubscription = await DeseretBook.user(user)
          .subscription()
          .create(createForm)

        // Catch any errors returned by the subscription
        if (createdSubscription.errors) {
          throw new FullError(createdSubscription)
        }

        createdSubscriptions.push(createdSubscription)

        onCreateSuccessful(createdSubscriptions)
        this.setState({ isCreating: false })
      } catch (error) {
        const { message, errors } = error

        logger.critical(
          `Unable to create a new subscription: "${formatErrorMessage(message, errors)}"`,
          { errors },
        )

        onCreateFailure(formatErrorMessage(message, errors))
        this.setState({ isCreating: false })
      }
    })
  }

  /**
   * Pulls the most recent wallet payment sources for the user from the API. This should be called
   * if you know (or are pretty sure) that the user's wallet have changed.
   *
   * @param {function} [callback=noop] - Callback that is run once payment sources are update
   *  successfully.
   */
  refreshPaymentSources(callback = () => {}) {
    const { user, onRefreshPaymentSources } = this.props

    this.setState({ isFetchingPaymentSources: true }, async () => {
      try {
        const { walletPaymentSources } = await DeseretBook.user(user)
          .walletPaymentSource()
          .all({ perPage: 1000, page: 1 })

        this.setState(
          {
            paymentSources: walletPaymentSources,
            isFetchingPaymentSources: false,
          },
          () => {
            if (typeof callback === 'function') {
              callback(walletPaymentSources)
            }

            if (typeof onRefreshPaymentSources === 'function') {
              onRefreshPaymentSources(walletPaymentSources)
            }
          },
        )
      } catch ({ message, errors }) {
        this.setState({ isFetchingPaymentSources: false })
        logger.error(
          `Unable to fetch wallet payment sources: "${formatErrorMessage(message, errors)}"`,
          { errors, user },
        )
      }
    })
  }

  /**
   * Determines how to render the component and what data to pass to children.
   *
   * @returns {function} Component
   */
  render() {
    const { user, children } = this.props

    const isLoading = [
      this.state.isFetchingHealth,
      this.state.isFetchingAddresses,
      this.state.isFetchingEstimates,
      this.state.isFetchingPaymentSources,
      this.state.isFetchingSubscriptionPlans,
      this.state.isFetchingGiftCard,
      this.state.isCreating,
    ].some(Boolean)

    const selectedEstimate = this.state.estimates[this.state.form.btPlanId]

    return children({
      ...this.state,
      user,
      isLoading,
      selectedEstimate,
      onFormChange: this.onFormChange,
      onGiftCardFormChange: this.onGiftCardFormChange,
      applyGiftCard: this.applyGiftCard,
      removeGiftCard: this.removeGiftCard,
      createSubscription: this.createSubscription,
      refreshAddresses: this.refreshAddresses,
      refreshPaymentSources: this.refreshPaymentSources,
    })
  }
}

SubscriptionFormManager.propTypes = {
  subscriptionType: PropTypes.string.isRequired,
  children: PropTypes.func.isRequired,
  user: PropTypes.shape({
    id: PropTypes.number,
    platinum: PropTypes.bool,
  }).isRequired,
  onCreateSuccessful: PropTypes.func,
  onCreateFailure: PropTypes.func,
  onAddGiftCard: PropTypes.func,
  onAddGiftCardFailure: PropTypes.func,
  onRemoveGiftCard: PropTypes.func,
  onRefreshAddresses: PropTypes.func,
  onRefreshPaymentSources: PropTypes.func,
  onFormChange: PropTypes.func,
  promoCode: PropTypes.string,
}

SubscriptionFormManager.defaultProps = {
  onCreateSuccessful: () => {},
  onCreateFailure: () => {},
  onAddGiftCard: () => {},
  onAddGiftCardFailure: () => {},
  onRemoveGiftCard: () => {},
  onRefreshAddresses: () => {},
  onRefreshPaymentSources: () => {},
  onFormChange: () => {},
  promoCode: null,
}

export default SubscriptionFormManager
